import * as React from 'react';
import { useNavigate } from 'react-router-dom';

import AlertActionError from './AlertActionError';
import AlertReconnectFlow from './AlertReconnectFlow';

import type { AlertRowAction } from '../../utils/alertAction';

/**
 * One message for every failure: a reconnect targets a connection the user
 * already has, so the codes `useAccountLink` can return here all mean the same
 * thing to them — it did not work, try again.
 */
const RECONNECT_ERROR_MESSAGE = 'We could not reconnect that account. Please try again.';

export interface IAlertRowAction {
  /**
   * Runs an already-resolved action, so every surface shares one navigation and
   * reconnect execution path. Returns false for `null`, which leaves the click
   * to the surface — selecting the row, opening it, or whatever else it does
   * with an ordinary alert.
   */
  runAction: (action: AlertRowAction | null) => boolean;
  /**
   * Render inside the alert surface. Carries the in-flight reconnect and any
   * error it produced; nothing is visible unless a reconnect has failed.
   */
  alertActionSlot: React.ReactNode;
}

/**
 * Executes an alert row's resolved action, so every surface listing alerts
 * behaves the same on click without repeating the navigation and Plaid wiring.
 *
 * Reconnects mount a single {@link AlertReconnectFlow} keyed to the connection
 * rather than one Plaid state machine per row — the hook behind it opens a
 * query and a Plaid Link instance, which a list cannot afford per item.
 *
 * @returns `runAction`, to call from a row's click handler — it reports back
 *   whether it handled the click — and `alertActionSlot`, which the surface has
 *   to render for a reconnect to run at all.
 */
export function useAlertRowAction(): IAlertRowAction {
  const navigate = useNavigate();
  const [reconnectingId, setReconnectingId] = React.useState<string | null>(null);
  const [hasReconnectError, setHasReconnectError] = React.useState(false);

  const runAction = React.useCallback(
    (action: AlertRowAction | null): boolean => {
      if (!action) {
        return false;
      }

      if (action.kind === 'navigate') {
        void navigate(action.path);
        return true;
      }

      setHasReconnectError(false);
      setReconnectingId(action.bankConnectionId);
      return true;
    },
    [navigate]
  );

  const handleDone = React.useCallback(() => setReconnectingId(null), []);

  const handleError = React.useCallback(() => {
    setHasReconnectError(true);
    // Unmounts the flow, so clicking the same row again mounts a fresh one.
    setReconnectingId(null);
  }, []);

  const alertActionSlot = (
    <>
      {reconnectingId && (
        <AlertReconnectFlow
          key={reconnectingId}
          bankConnectionId={reconnectingId}
          onDone={handleDone}
          onError={handleError}
        />
      )}
      {hasReconnectError && <AlertActionError message={RECONNECT_ERROR_MESSAGE} className="mx-4 mb-3" />}
    </>
  );

  return { runAction, alertActionSlot };
}
